import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info, Search, Shield, Users, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

import { Role } from "@/services/api/RoleService";
import { RoleNome, PermissaoEnum, PermissaoLabelsMap, RoleLabelsMap } from "@workflow/types";
import { permissaoService } from "@/services/api/permissao.service";
import { pessoaService } from "@/services/api/PessoaService";

import { Person } from "@/components/shared/person";
import { useIsMobile } from "@/hooks/use-mobile";
import { normalizeText } from "@/components/campos-formulario/novos/autocomplete/normalizer";
import {
  getPermissaoModulo,
  PERMISSAO_MODULO_ORDER,
  PERMISSAO_MODULO_STYLE,
  PermissaoModulo,
} from "@/lib/permissao-modulo";
import { cn } from "@/lib/utils";

interface ViewRolePermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
}

type StatusFilter = "all" | "selected";
type TabValue = "permissoes" | "usuarios";

const PERM_STALE = 30 * 60 * 1000; // 30min — lista mestra de permissões muda raramente
const USERS_STALE = 60 * 1000; // 60s

/**
 * Visualização (somente leitura) de uma função: suas permissões (agrupadas por
 * módulo) e os usuários ATIVOS que a possuem. A lista de usuários só é buscada
 * quando a aba "Usuários" é aberta (lazy).
 */
export function ViewRolePermissionsDialog({
  open,
  onOpenChange,
  role,
}: ViewRolePermissionsDialogProps) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<TabValue>("permissoes");

  // Estado da aba de permissões
  const [permSearch, setPermSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("selected");

  // Estado da aba de usuários
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (open) {
      setTab("permissoes");
      setPermSearch("");
      setStatusFilter("selected");
      setUserSearch("");
    }
  }, [open]);

  const roleLabel = role ? RoleLabelsMap[role.nome as keyof typeof RoleLabelsMap] || role.nome : "";
  const rolePermissionNomes = useMemo(() => role?.permissoes?.map((p) => p.nome) ?? [], [role]);

  // --- Lista mestra de permissões (lazy: só quando o dialog abre) ---
  const { data: allPermissions = [] } = useQuery({
    queryKey: ["permissoes", "all"],
    queryFn: () => permissaoService.getPermissions(),
    enabled: open,
    staleTime: PERM_STALE,
  });

  // --- Usuários ativos da função (lazy: só quando a aba Usuários é aberta) ---
  const {
    data: usuarios = [],
    isLoading: isLoadingUsuarios,
    isError: isErrorUsuarios,
  } = useQuery({
    queryKey: ["role-pessoas-ativas", role?.nome],
    queryFn: () => pessoaService.getPessoasByRole(role!.nome as RoleNome),
    enabled: open && tab === "usuarios" && !!role,
    staleTime: USERS_STALE,
  });

  // ===================== Aba Permissões =====================
  const selectedNomesSet = useMemo(() => new Set(rolePermissionNomes), [rolePermissionNomes]);

  const sortedPermissions = useMemo(() => {
    return [...allPermissions].sort((a, b) => {
      const aNome = PermissaoLabelsMap[a.nome as PermissaoEnum]?.nome || a.nome;
      const bNome = PermissaoLabelsMap[b.nome as PermissaoEnum]?.nome || b.nome;
      return aNome.localeCompare(bNome);
    });
  }, [allPermissions]);

  const searchMatches = useMemo(() => {
    const terms = normalizeText(permSearch).split(/\s+/).filter(Boolean);
    return sortedPermissions.filter((p) => {
      if (terms.length === 0) return true;
      const detalhe = PermissaoLabelsMap[p.nome as PermissaoEnum];
      const content = normalizeText(`${detalhe?.nome || ""} ${detalhe?.descricao || ""} ${p.nome}`);
      return terms.every((t) => content.includes(t));
    });
  }, [sortedPermissions, permSearch]);

  const visiblePermissions = useMemo(() => {
    if (statusFilter === "selected")
      return searchMatches.filter((p) => selectedNomesSet.has(p.nome));
    return searchMatches;
  }, [searchMatches, statusFilter, selectedNomesSet]);

  const groupedPermissions = useMemo(() => {
    const map = new Map<PermissaoModulo, typeof visiblePermissions>();
    for (const p of visiblePermissions) {
      const modulo = getPermissaoModulo(p.nome);
      const arr = map.get(modulo);
      if (arr) arr.push(p);
      else map.set(modulo, [p]);
    }
    return PERMISSAO_MODULO_ORDER.filter((m) => map.has(m)).map((m) => ({
      modulo: m,
      style: PERMISSAO_MODULO_STYLE[m],
      permissoes: map.get(m)!,
    }));
  }, [visiblePermissions]);

  const grantedCount = rolePermissionNomes.length;

  // ===================== Aba Usuários =====================
  const usuariosFiltrados = useMemo(() => {
    const terms = normalizeText(userSearch).split(/\s+/).filter(Boolean);
    const sorted = [...usuarios].sort((a, b) => a.nome.localeCompare(b.nome));
    if (terms.length === 0) return sorted;
    return sorted.filter((u) => {
      const content = normalizeText(`${u.nome} ${u.email}`);
      return terms.every((t) => content.includes(t));
    });
  }, [usuarios, userSearch]);

  const usuariosCount = role?.totalPessoasAtivas ?? usuarios.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 border-border bg-card p-0",
          isMobile
            ? "h-[100dvh] w-full max-w-none rounded-none border-0"
            : "max-h-[85vh] sm:max-w-xl",
        )}
        style={isMobile ? { transform: "none", top: 0, left: 0, bottom: 0 } : undefined}
      >
        <DialogHeader className="shrink-0 border-b border-border/50 px-6 pb-4 pt-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Shield className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="truncate text-xl">{roleLabel || "Função"}</DialogTitle>
              <DialogDescription>Permissões e usuários deste cargo.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as TabValue)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0 px-6 pt-3">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="permissoes" className="gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Permissões
                <span className="text-muted-foreground">({grantedCount})</span>
              </TabsTrigger>
              <TabsTrigger value="usuarios" className="gap-1.5">
                <Users className="h-3.5 w-3.5" /> Usuários
                <span className="text-muted-foreground">({usuariosCount})</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ============ ABA: PERMISSÕES ============ */}
          <TabsContent
            value="permissoes"
            className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <div className="shrink-0 space-y-3 border-b border-border/50 bg-muted/5 px-6 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Ex: 'cria planilha' ou 'job delete'..."
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  className="h-10 bg-background pl-9 pr-9 shadow-inner"
                />
                {permSearch && (
                  <button
                    type="button"
                    onClick={() => setPermSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Limpar busca"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="inline-flex rounded-lg bg-muted p-0.5 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setStatusFilter("selected")}
                  className={cn(
                    "rounded-md px-3 py-1.5 transition-colors",
                    statusFilter === "selected"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Concedidas ({grantedCount})
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter("all")}
                  className={cn(
                    "rounded-md px-3 py-1.5 transition-colors",
                    statusFilter === "all"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Todas
                </button>
              </div>
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto">
              {visiblePermissions.length === 0 ? (
                <div className="flex flex-col items-center gap-3 p-12 text-center text-muted-foreground">
                  <div className="rounded-full bg-muted p-3">
                    <Shield className="h-6 w-6 opacity-40" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {statusFilter === "selected"
                        ? "Nenhuma permissão concedida"
                        : "Nenhum resultado"}
                    </p>
                    <p className="text-xs opacity-60">
                      {statusFilter === "selected"
                        ? "Esta função não possui permissões."
                        : "Tente buscar por termos diferentes."}
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  {groupedPermissions.map(({ modulo, style, permissoes }) => (
                    <div key={modulo}>
                      <div className="sticky top-0 z-10 flex items-center gap-2 border-y border-border/50 bg-muted/60 px-6 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-muted/40">
                        <span className={cn("h-2 w-2 shrink-0 rounded-full", style.dot)} />
                        <span
                          className={cn(
                            "text-[11px] font-bold uppercase tracking-wider",
                            style.text,
                          )}
                        >
                          {style.label}
                        </span>
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {permissoes.length}
                        </span>
                      </div>

                      <div className="divide-y divide-border/50">
                        {permissoes.map((permission) => {
                          const isSelected = selectedNomesSet.has(permission.nome);
                          const detalhe = PermissaoLabelsMap[permission.nome as PermissaoEnum];
                          return (
                            <div
                              key={permission.id}
                              className={cn(
                                "flex items-start gap-3 px-6 py-3 transition-colors",
                                isSelected && "bg-primary/[0.04]",
                              )}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled
                                tabIndex={-1}
                                aria-hidden
                                className="pointer-events-none mt-0.5 disabled:opacity-100 data-[state=checked]:opacity-100"
                              />
                              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <Label
                                  className={cn(
                                    "text-sm font-semibold leading-snug transition-colors",
                                    isSelected ? "text-primary" : "text-foreground",
                                  )}
                                >
                                  {detalhe?.nome || permission.nome}
                                </Label>
                                <span className="text-xs leading-relaxed text-muted-foreground">
                                  {detalhe?.descricao || "Sem descrição disponível."}
                                </span>
                                <code className="mt-1 w-fit rounded bg-muted/30 px-1 font-mono text-[9px] text-muted-foreground/40">
                                  {permission.nome}
                                </code>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ ABA: USUÁRIOS ============ */}
          <TabsContent
            value="usuarios"
            className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <div className="shrink-0 space-y-3 border-b border-border/50 bg-muted/5 px-6 py-3">
              <div className="flex items-start gap-2 rounded-md border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-[11px] text-blue-700 dark:text-blue-300">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Exibindo apenas usuários ativos. Pessoas inativas não aparecem nesta lista.
                </span>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="h-10 bg-background pl-9 pr-9 shadow-inner"
                />
                {userSearch && (
                  <button
                    type="button"
                    onClick={() => setUserSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Limpar busca"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="custom-scrollbar flex-1 overflow-y-auto">
              {isLoadingUsuarios ? (
                <div className="p-12 text-center text-sm text-muted-foreground">
                  Carregando usuários...
                </div>
              ) : isErrorUsuarios ? (
                <div className="p-12 text-center text-sm text-destructive">
                  Não foi possível carregar os usuários.
                </div>
              ) : usuariosFiltrados.length === 0 ? (
                <div className="flex flex-col items-center gap-3 p-12 text-center text-muted-foreground">
                  <div className="rounded-full bg-muted p-3">
                    <Users className="h-6 w-6 opacity-40" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {userSearch ? "Nenhum resultado" : "Nenhum usuário ativo"}
                    </p>
                    <p className="text-xs opacity-60">
                      {userSearch
                        ? "Tente buscar por outro termo."
                        : "Nenhuma pessoa ativa possui esta função."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {usuariosFiltrados.map((u) => (
                    <Person.Root
                      key={u.id}
                      pessoa={u}
                      className="flex items-center gap-3 px-6 py-3"
                    >
                      <Person.Avatar className="h-9 w-9" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <Person.Name className="truncate text-sm font-semibold text-foreground" />
                        <Person.Email className="truncate text-[11px] text-muted-foreground" />
                      </div>
                      <Person.Status />
                    </Person.Root>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
